import React, { useState } from 'react';
import { MessageCircle, X, Send, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import axios from 'axios';

const FloatingLiveChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState('initial'); // 'initial', 'form', 'connecting', 'connected'
  const [formData, setFormData] = useState({
    email: '',
    message: '',
    name: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      await axios.post(`${API}/api/live-chat/help-request`, {
        email: formData.email,
        message: formData.message,
        name: formData.name,
        timestamp: new Date().toISOString(),
        source: 'floating_chat'
      });

      setSubmitMessage('Your message has been sent to our support team. You will receive an email response shortly.');
      setStep('connecting');
      
      // Show connecting message for 3 seconds, then show Tawk.to option
      setTimeout(() => {
        setStep('connected');
      }, 3000);

    } catch (error) {
      console.error('Error submitting help request:', error);
      setSubmitMessage('Sorry, there was an error sending your message. Please try again or contact us directly at helpdesk.formforgepro@gmail.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConnectToLiveChat = () => {
    // Hide our chat widget
    setIsOpen(false);
    
    // Trigger Tawk.to widget to open
    if (window.Tawk_API) {
      window.Tawk_API.maximize();
      window.Tawk_API.showWidget();
    } else {
      // Fallback if Tawk.to hasn't loaded yet
      setTimeout(() => {
        if (window.Tawk_API) {
          window.Tawk_API.maximize();
          window.Tawk_API.showWidget();
        }
      }, 1000);
    }
  };

  const resetChat = () => {
    setStep('initial');
    setFormData({ email: '', message: '', name: '' });
    setSubmitMessage('');
  };

  return (
    <>
      {/* Floating Chat Button */}
      <div className="fixed bottom-4 sm:bottom-6 right-20 sm:right-24 z-50">
        {!isOpen && (
          <Button
            onClick={() => setIsOpen(true)}
            className="bg-teal-500 hover:bg-teal-600 text-white rounded-full p-3 sm:p-4 shadow-lg hover:shadow-xl transition-all duration-300 animate-pulse"
            size="lg"
          >
            <MessageCircle className="h-5 w-5 sm:h-6 sm:w-6" />
            <span className="ml-2 hidden sm:inline">Get Live Help</span>
          </Button>
        )}

        {/* Chat Window */}
        {isOpen && (
          <Card className="w-80 sm:w-96 shadow-2xl border-2 border-teal-200">
            <CardHeader className="bg-teal-500 text-white rounded-t-lg">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold">
                  {step === 'initial' && 'Live Help'}
                  {step === 'form' && 'Contact Support'}
                  {step === 'connecting' && 'Connecting...'}
                  {step === 'connected' && 'Live Agent Available'}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsOpen(false)}
                  className="text-white hover:bg-teal-600 p-1"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>

            <CardContent className="p-4">
              {step === 'initial' && (
                <div className="space-y-4">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <MessageCircle className="w-8 h-8 text-teal-600" />
                    </div>
                    <h3 className="font-semibold text-gray-800 mb-2">
                      Welcome to FormForgePro Support
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      We're here to help! Get assistance with your legal documents, account questions, or technical support.
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Button
                      onClick={() => setStep('form')}
                      className="w-full bg-teal-500 hover:bg-teal-600 text-white"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Send Message to Support
                    </Button>
                    
                    <Button
                      onClick={handleConnectToLiveChat}
                      variant="outline"
                      className="w-full border-teal-300 text-teal-600 hover:bg-teal-50"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Click Live Chat
                    </Button>
                  </div>
                </div>
              )}

              {step === 'form' && (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <Input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Your full name"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address *
                    </label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="your.email@example.com"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      How can we help you?
                    </label>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      placeholder="Please describe your question or issue..."
                      rows={4}
                      required
                    />
                  </div>

                  {submitMessage && (
                    <div className={`text-sm p-3 rounded-lg ${
                      submitMessage.includes('error') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                    }`}>
                      {submitMessage}
                    </div>
                  )}

                  <div className="flex space-x-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={resetChat}
                      className="flex-1"
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 bg-teal-500 hover:bg-teal-600"
                    >
                      {isSubmitting ? (
                        'Sending...'
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              )}

              {step === 'connecting' && (
                <div className="text-center py-6">
                  <div className="animate-spin w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                  <h3 className="font-semibold text-gray-800 mb-2">
                    Message Sent Successfully!
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Your support request has been sent to our help desk. You'll receive an email response shortly.
                  </p>
                  <p className="text-sm text-teal-600 mt-2">
                    Checking for available live agents...
                  </p>
                </div>
              )}

              {step === 'connected' && (
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <MessageCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-2">
                      Live Agent Available!
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      A human specialist is ready to help you. Connect now for real-time assistance.
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Button
                      onClick={handleConnectToLiveChat}
                      className="w-full bg-green-500 hover:bg-green-600 text-white"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Click Live Chat
                    </Button>
                    <Button
                      onClick={resetChat}
                      variant="outline"
                      className="w-full"
                    >
                      Back to Main Menu
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
};

export default FloatingLiveChat;